// In a web `?live=<config>` tab, every same-origin `/api/*` request carries an
// `X-Tiger-Tab: <config>` header. The webserver uses it to:
//   * give the tab its OWN backend session (session id = cookie:config), so its
//     live/replay toggle doesn't bleed into the alarm-interface tab — which shares
//     the auth cookie and would otherwise read the tab's (empty) replay copy; and
//   * scope turbine lists (Load Data / channel / export dialogs) to that one
//     turbine, matching the scoped views tree.
// EVERY web page (main window included) additionally stamps `X-Tiger-Hidden: 1`
// on /api requests fired while the document is hidden (minimized / background
// tab). Browsers keep throttled timers running in hidden tabs, so their polls
// continue — which must keep the SESSION alive (no reap while merely
// backgrounded) but must NOT count as "someone is watching a live screen":
// without the stamp a minimized main window's ~1/min throttled
// update_layout_items poll kept resetting the remote-sample idle clock and the
// backend never dropped to the slow idle cadence.
// Desktop (Tauri) sends no headers and is unaffected.

const TAB_HEADER = "X-Tiger-Tab";
const HIDDEN_HEADER = "X-Tiger-Hidden";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** The `?live=<config>` token in the current URL, or null when absent/empty. */
function liveTabConfig(): string | null {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get("live");
  return v && v.length > 0 ? v : null;
}

/** Propagate this tab's `?live=<config>` flag to a spawned tool-window URL
 *  (graph / data grid / diagnostics). Those windows are separate documents, so
 *  they must carry the flag to install their own header wrapper and thereby join
 *  the same isolated, turbine-scoped session as the opener. No-op when the opener
 *  isn't a `?live=` tab (desktop / plain alarm interface). */
export function appendLiveParam(url: string): string {
  const config = liveTabConfig();
  if (!config) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}live=${encodeURIComponent(config)}`;
}

/** True for a same-origin Tiger API request (`/api/...`), the only requests we
 *  stamp. Handles string, URL, and Request inputs (absolute or relative). */
function isApiRequest(input: RequestInfo | URL): boolean {
  const url =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.pathname
        : input.url;
  return typeof url === "string" && url.includes("/api/");
}

/** Install a one-time `window.fetch` wrapper that stamps `X-Tiger-Tab` on
 *  `/api/*` requests when this is a web `?live=` tab, and `X-Tiger-Hidden`
 *  when the document is hidden (all web pages — see the header comment).
 *  No-op on desktop or when called outside a browser. */
export function installLiveTabFetch(): void {
  if (typeof window === "undefined" || isTauri()) return;
  const config = liveTabConfig();

  const original = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    if (!isApiRequest(input)) return original(input, init);
    const hidden = typeof document !== "undefined" && document.visibilityState === "hidden";
    if (!config && !hidden) return original(input, init);
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    if (config) headers.set(TAB_HEADER, config);
    if (hidden) headers.set(HIDDEN_HEADER, "1");
    return original(input, { ...init, headers });
  };
}
